//  External
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

//  Internal
import { ConnectNotificationsService } from '../../../common/services/requests/connect-notifications/connect-notifications.service';
import { NotificationDrawerStatus, NotificationsService } from '../../../common/services/process/notifications/notifications.service';
import { ToastService } from '../../../common/services/global/toast/toast.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { DevicesValuesService } from '../../../common/values/devices.values';
import { ValuesService } from '../../../common/values/values.service';
import { UtilsCommonService } from '../../../common/utils/utils-common.service';
import { DevicesService } from '../../../common/services/process/devices/devices.service';
import { TasksMgmtService } from '../../../common/services/requests/connect-tasks-mgmt-service/connect-tasks-mgmt.service';
import { ModalName, ModalRoutelessService } from '../../../common/components/ui/ui-modal-routeless/modal.routeless.service';
import { AdobeDataLayerService } from '../../../common/services/core/adobe.datalayer.service';
import { ModalContainerOptions, ModalSize } from '../../../common/services/global/modal/Modal.model';
import { INotificationsService } from '../../../common/services/interfaces/inotifications/inotifications.service';
import { NotificationsFunctions } from '../../../common/services/global/events/events.model';

@Component({
    selector: 'app-notification-details',
    templateUrl: './notification-details.component.html',
    styleUrls: ['./notification-details.component.scss']
})

export class NotificationDetailsComponent implements OnInit {

    @Input() notification: any;
    @Output() back: EventEmitter<{}> = new EventEmitter();
    exceptions: any;
    private readonly onDestroy$: Subject<void> = new Subject<void>();
    public notificationtipsAreAnArray = false;

    updateErrorCodes = {
        1128: 'devices.details.update.error.running',
        1129: 'devices.details.update.error.pending'
    };

    notificationsCta = {
        [NotificationsFunctions.ALLOW_ACCESS_PAGE]: () => this.allowAccessPage(),
        [NotificationsFunctions.ANTITHEFT_COMMANDS]: () => this.navigateToAntitheft(this.devicesValuesService.antitheftScenarios.full),
        [NotificationsFunctions.ANTITHEFT_WIPE]: () => this.navigateToAntitheft(this.devicesValuesService.antitheftScenarios.wipe),
        [NotificationsFunctions.ANTITHEFT_LOCK]: () => this.navigateToAntitheft(this.devicesValuesService.antitheftScenarios.lock),
        [NotificationsFunctions.ANTITHEFT_SNAP]: () => this.navigateToAntitheft(this.devicesValuesService.antitheftScenarios.snap),
        [NotificationsFunctions.ANTITHEFT_MAP]: () => this.navigateToAntitheft(this.devicesValuesService.antitheftScenarios.locate),
        [NotificationsFunctions.BLOCK_DEVICE]: () => this.blockDevice(),
        [NotificationsFunctions.DEVICE_VIEW]: () => this.deviceView(),
        [NotificationsFunctions.VULNERABILITY_VIEW]: () => this.vulnerabilityView(),
        [NotificationsFunctions.UPDATE_BOX]: () => this.updateBox(),
        [NotificationsFunctions.INSTALL_PROTECTION]: () => this.installProtection(),
        [NotificationsFunctions.SCAN_DEVICE]: () => this.scanDevice(),
        [NotificationsFunctions.VIEW_EXCEPTIONS]: () => this.viewExceptions()
    };

    constructor(
        private readonly connectNotificationsService: ConnectNotificationsService,
        private readonly notificationsService: NotificationsService,
        private readonly router: Router,
        private readonly toastService: ToastService,
        private readonly utilsCommonService: UtilsCommonService,
        private readonly valuesService: ValuesService,
        private readonly deviceDetectorService: DeviceDetectorService,
        private readonly devicesValuesService: DevicesValuesService,
        private readonly devicesService: DevicesService,
        private readonly translateService: TranslateService,
        private readonly tasksMgmtService: TasksMgmtService,
        private readonly adobeDataLayerService: AdobeDataLayerService,
        private readonly modalRoutelessService: ModalRoutelessService,
        private readonly iNotificationsService: INotificationsService
    ) {}

    ngOnInit() {
        // omniture start
        const type = this.notification?.app_fields?.notification_params?.notification_type ?? undefined;
        const subType = this.notification?.app_fields?.notification_params?.notification_subtype ?? undefined;
        this.notificationtipsAreAnArray = typeof this.notification.interface.tips === 'object';

        if (type && subType) {
            this.notificationsService.setNotificationType(type, subType);
            this.adobeDataLayerService.setPageSection(this.computeAdobeExtraSection(type, subType), 0, true);
        }

        if (this.notification.ack === false) {
            this.notificationsService.readNotification(this.notification)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe();
        }
    }

    private computeAdobeExtraSection(type, subtype) {
        return this.valuesService.omnitureNotificationsSections.notifications.concat(':', type, ':', subtype);
    }

    /**
     * Go back to notifications list and change drawer status
     * @public
     * @memberof NotificationDetailsComponent
     */
    public onBack(): void {
        this.notificationsService.setDrawerStatus(NotificationDrawerStatus.LIST);
        this.adobeDataLayerService.setPageSection(this.valuesService.omnitureNotificationsSections.notifications, 0, true);
        this.back.emit(true);
    }

    //  delete one notification functionality
    public deleteNotification() {
        this.connectNotificationsService.deleteSingleNotification(this.notification.app_fields.notification_id)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
            next: () => {
                const ntfs = this.notificationsService.get().list;
                for (const i in ntfs) {
                    if (ntfs[i].app_fields.notification_id === this.notification.app_fields.notification_id) {
                        ntfs.splice(i, 1);
                    }
                }
                this.onBack();
            },
            error: err => {
                this.toastService.error('There was an error deleting your notification.');
            }
        });
    }

    // ! de testat cand va merge redirectul
    private allowAccessPage() {
        const data = {
            box_id: this.notification.interface.box_device_id,
            device_id: this.notification.interface.device_id,
            threat_id: this.notification.interface.threat_id
        };

        this.devicesService.setException(data)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
            next: () => {
                this.iNotificationsService.changeNotificationInterfaceMainLinkInfoForException(this.notification.interface);
                this.toastService.success(this.translateService.instant('notifications.main.link.exception.success'));
            },
            error: () => {
                this.toastService.error(this.translateService.instant('notifications.main.link.exception.error'));
            }
        });
    }

    private navigateToAntitheft(scenario) {
        const style = {
            size: ModalSize.LG,
            exceptionClass: this.deviceDetectorService.isMobile() ? 'light-close' : ''
        };
        const contentOptions = {
            deviceId: this.notification.interface.device_id,
            scenario: scenario ?? this.devicesValuesService.antitheftScenarios.full
        };
        this.modalRoutelessService.open(ModalName.antitheftModal, style, contentOptions);
    }

    private blockDevice() {
        this.connectNotificationsService.blockDevice({ device_id: this.notification.interface.device_id, nb: 1 })
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
            next: () => {
                this.toastService.success('Device has been successfully blocked');
            },
            error: () => {
                this.toastService.error('There was a problem with your request. Please try again later');
            }
        });
    }

    private deviceView() {
        const idHash = this.utilsCommonService.crc32Convert_old(this.notification.interface.device_id);
        this.router.navigate([this.valuesService.centralPaths.devices.path, idHash, this.valuesService.centralPaths.devices.details.id]);
    }

    private vulnerabilityView() {
        const containerOptions = {
            size: ModalSize.XL,
            exceptionClass: this.deviceDetectorService.isMobile() ? 'light-close' : ''
        };
        // trebuie facut ca la antitheft, cu scenario momentan doar deschide modala de vulnerability
        this.modalRoutelessService.open(ModalName.vulnerabilityScanModal, containerOptions, { deviceId: this.notification.interface.device_id });
    }

    private updateBox() {
        const info: any = {};
        info.task = this.devicesValuesService.tasksName.update;
        info.app_id = this.valuesService.appBOX2;

        return this.tasksMgmtService.sendTask(this.notification.interface.device_id, info, {})
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
            next: () => {
                this.toastService.success(this.translateService.instant('malwareScan.modal.scan.command.sent.hint'));
            },
            error: err => {
                if (this.updateErrorCodes[err.code]) {
                    this.toastService.error(this.translateService.instant(this.updateErrorCodes[err.code]));
                    return;
                }
                this.toastService.error(this.translateService.instant('devices.settings.error'));
            }
        });
    }

    /**
     * Opens install modal
     * @private
     * @memberof NotificationDetailsComponent
     */
    private installProtection(): void {
        const containerOptions: ModalContainerOptions = {
            size: ModalSize.LG
        };
        this.modalRoutelessService.open(ModalName.installModal, containerOptions, null);
    }

    private scanDevice() {
        const containerOptions = {
            size: ModalSize.XL,
            exceptionClass: this.deviceDetectorService.isMobile() ? 'light-close' : ''
        };
        this.modalRoutelessService.open(ModalName.malwareScanModal, containerOptions, { deviceId: this.notification.interface.device_id })
    }

    private viewExceptions() {
        const notificationBox = this.devicesService.retrieveDeviceById(this.notification.interface.box_device_id);
        this.modalRoutelessService.open(ModalName.whitelistModal, { size: ModalSize.SM }, { device: notificationBox });
    }

    callSpecificFunction(functionName) {
        this.notificationsCta[functionName]();
    }
}
